import React, { useContext, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { Menu, X, Briefcase, User, ClipboardList } from "lucide-react";

const Navbar = () => {
  const { user } = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);

  const activeClass = "text-blue-600 font-semibold";
  const inactiveClass = "text-slate-400 hover:text-white transition-colors";

  const toggleMenu = () => setIsOpen(!isOpen);

  const navLinks = [
    { name: "Home", path: "/", icon: <Briefcase size={18} /> },
    { name: "Applications", path: "/applications", icon: <ClipboardList size={18} /> },
    { name: "Profile", path: "/profile", icon: <User size={18} /> },
  ];

  return (
    <>
      <nav className="glass-nav sticky top-0 z-50 w-full px-6 py-4">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <div className="bg-blue-600 p-2 rounded-lg group-hover:rotate-12 transition-transform">
            <Briefcase className="text-white w-6 h-6" />
          </div>
          <span className="text-xl font-bold text-white tracking-tight">
            Job<span className="text-blue-500">Portal</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                `flex items-center gap-2 text-sm font-medium ${
                  isActive ? activeClass : inactiveClass
                }`
              }
            >
              {link.icon}
              {link.name}
            </NavLink>
          ))}
        </div>

        {/* User Profile (Desktop) */}
        <div className="hidden md:flex items-center gap-4 pl-8 border-l border-slate-700">
          <div className="text-right">
            <p className="text-xs text-slate-400">Welcome,</p>
            <p className="text-sm font-semibold text-white">{user.name}</p>
          </div>
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
            {user.name.charAt(0)}
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden text-white p-2 hover:bg-slate-700 rounded-lg transition-colors"
          onClick={toggleMenu}
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

    </nav>
 
     {/* Mobile Drawer - Moved outside <nav> to avoid background filter issues */}
     <div
       className={`fixed inset-y-0 right-0 w-[280px] sm:w-80 bg-[#0f172a] shadow-[0_0_50px_rgba(0,0,0,0.5)] transform transition-transform duration-300 ease-in-out z-[100] md:hidden ${
         isOpen ? "translate-x-0" : "translate-x-full"
       } border-l border-slate-800`}
     >
       <div className="p-6 flex flex-col h-full">
         <div className="flex justify-between items-center mb-10 pb-6 border-b border-slate-800">
           <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-2">
             <div className="bg-blue-600 p-1.5 rounded-lg">
               <Briefcase className="text-white w-5 h-5" />
             </div>
             <span className="font-bold text-white text-xl tracking-tight">
               Job<span className="text-blue-500 font-extrabold">Portal</span>
             </span>
           </Link>
           <button
             onClick={toggleMenu}
             className="text-slate-400 hover:text-white p-2 hover:bg-slate-800 rounded-lg transition-colors border border-slate-800"
           >
             <X className="w-6 h-6" />
           </button>
         </div>
 
         <div className="flex flex-col gap-2">
           {navLinks.map((link) => (
             <NavLink
               key={link.path}
               to={link.path}
               onClick={() => setIsOpen(false)}
               className={({ isActive }) =>
                 `flex items-center gap-4 py-4 px-4 rounded-xl text-base font-bold transition-all ${
                   isActive ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20" : "text-slate-300 hover:bg-slate-800"
                 }`
               }
             >
               <span className="shrink-0 text-inherit">
                 {link.icon}
               </span>
               {link.name}
             </NavLink>
           ))}
         </div>
 
         <div className="mt-auto pt-8 border-t border-slate-800">
           <div className="bg-slate-800/50 p-4 rounded-2xl flex gap-4 items-center">
             <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg text-lg">
               {user.name.charAt(0)}
             </div>
             <div className="overflow-hidden">
               <p className="text-sm font-bold text-white truncate">{user.name}</p>
               <p className="text-xs text-slate-400 truncate">{user.email}</p>
             </div>
           </div>
         </div>
       </div>
     </div>
 
     {/* Backdrop Overlay */}
     {isOpen && (
       <div
         className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-[90] md:hidden"
         onClick={toggleMenu}
       ></div>
     )}
   </>
 );
};

export default Navbar;
